import React, { useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import Status from "components/atoms/Status/Status"
import { useAdSystems } from "hooks/useAdSystems"
import { useCustomQuery } from "hooks/useCustomQuery"
import { AdCampaignsService } from "services/api-service"
import { AdCampaignType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Spinner from "ui/components/atoms/spinner"
import Tag from "ui/components/atoms/tag"
import { H3, LabelH2, P } from "ui/components/atoms/typography"
import Accordion from "ui/components/molecules/accordion"
import Alert from "ui/components/molecules/alert"
import Modal from "ui/components/molecules/modal"
import { adSystemLabel } from "utils/adSystems"
import { formatDate } from "utils/date"

import OfferDetailsCampaignForm from "./OfferDetailsCampaignForm"

const IN_PROGRESS_STATUSES: string[] = ["draft", "creating"]

const BLOCKING_STATUSES = ["draft", "creating", "active", "paused", "budget_exhausted", "rejected"]

const formatMoney = (value: number | null | undefined, currency: string) =>
  `${(value ?? 0).toFixed(2)} ${currency}`

const formatNumber = (value: number | null | undefined) => (value ?? 0).toLocaleString("pl-PL")

const OfferDetailsCampaigns = ({ offerId }: { offerId: string }) => {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [budgetCampaign, setBudgetCampaign] = useState<AdCampaignType | null>(null)
  const [budgetDaily, setBudgetDaily] = useState("")
  const [budgetLimit, setBudgetLimit] = useState("")

  const { data, isLoading } = useCustomQuery<AdCampaignType[]>(
    {
      key: ["offer-campaigns", offerId],
      refetchInterval: query => {
        const rows = query.state.data ?? []
        return rows.some(row => IN_PROGRESS_STATUSES.includes(row.status)) ? 3000 : false
      },
    },
    () => AdCampaignsService.getOfferCampaigns(offerId),
  )

  const { data: systems } = useAdSystems()

  const refresh = useCallback(() => {
    queryClient.refetchQueries({ queryKey: ["offer-campaigns", offerId] })
  }, [queryClient, offerId])

  const runAction = useCallback(
    async (campaign: AdCampaignType, action: () => Promise<unknown>, label: string) => {
      setLoadingId(campaign.id)
      setError("")
      try {
        await action()
        refresh()
      } catch (e: any) {
        SentryService.error(`[ERROR AdCampaignsService.${label}]:`, e)
        setError(e?.response?.data?.message ?? `Nie udało się wykonać akcji: ${label}`)
      } finally {
        setLoadingId(null)
      }
    },
    [refresh],
  )

  const deactivate = (campaign: AdCampaignType) =>
    runAction(campaign, () => AdCampaignsService.deactivateCampaign(campaign.id), "deactivate")

  const retry = (campaign: AdCampaignType) =>
    runAction(campaign, () => AdCampaignsService.retryCampaign(campaign.id), "retry")

  const renew = (campaign: AdCampaignType) => {
    const suggested = Math.round(campaign.dailyBudget * 30)
    const input = window.prompt("O ile złotych podnieść limit kampanii?", String(suggested))
    if (!input) return

    const amount = Number(input.replace(",", "."))
    if (!amount || amount <= 0) {
      setError("Podaj poprawną kwotę")
      return
    }

    return runAction(campaign, () => AdCampaignsService.renewCampaign(campaign.id, amount), "renew")
  }

  const openBudget = (campaign: AdCampaignType) => {
    setBudgetCampaign(campaign)
    setBudgetDaily(String(campaign.dailyBudget))
    setBudgetLimit(String(campaign.spendLimit))
  }

  const saveBudget = async () => {
    if (!budgetCampaign) return

    const daily = Number(budgetDaily.replace(",", "."))
    const limit = Number(budgetLimit.replace(",", "."))

    if (!daily || daily <= 0 || !limit || limit <= 0) {
      setError("Podaj poprawne kwoty")
      return
    }

    if (limit < daily) {
      setError("Limit całkowity nie może być niższy niż budżet dzienny")
      return
    }

    const campaign = budgetCampaign
    const minDailyBudget = systems?.find(item => item.system === campaign.system)?.minDailyBudget

    if (minDailyBudget !== undefined && daily < minDailyBudget) {
      setError(
        `Minimalny budżet dzienny w ${adSystemLabel(campaign.system)} to ${minDailyBudget} zł`,
      )
      return
    }

    const spent = campaign.metrics?.spend ?? 0

    if (limit < spent) {
      setError(`Limit nie może być niższy niż już wydane ${spent.toFixed(2)} ${campaign.currency}`)
      return
    }

    setBudgetCampaign(null)

    await runAction(
      campaign,
      () => AdCampaignsService.updateBudget(campaign.id, daily, limit),
      "updateBudget",
    )
  }

  const removeFailed = (campaign: AdCampaignType) =>
    runAction(campaign, () => AdCampaignsService.deleteCampaign(campaign.id), "delete")

  const hasBlocking = (data ?? []).some(campaign => BLOCKING_STATUSES.includes(campaign.status))

  return (
    <Accordion title="Kampanie reklamowe">
      {error && (
        <Alert $type="error" $small $marginBottom="S">
          {error}
        </Alert>
      )}

      {isLoading && <Spinner />}

      {!isLoading && (data ?? []).length === 0 && (
        <P $marginBottom="S">Ta oferta nie ma jeszcze żadnej kampanii.</P>
      )}

      {(data ?? []).map(campaign => {
        const spent = campaign.metrics?.spend ?? 0
        const progress = campaign.spendLimit
          ? Math.min(100, (spent / campaign.spendLimit) * 100)
          : 0

        return (
          <CampaignCard key={campaign.id}>
            <Row $alignItems="center" $justifyContent="space-between">
              <Status label={campaign.status} />
              <Tag
                $type="secondary"
                $showBorder={false}
                $background="background"
                $iconColor="primary"
                label={adSystemLabel(campaign.system)}
              />
            </Row>

            <LabelH2 $marginTop="XS">{campaign.name}</LabelH2>

            {campaign.pauseReason && (
              <P $marginTop="XXS" $small>
                {campaign.pauseReason === "offer_limited" && "Wstrzymana: oferta wygasła"}
                {campaign.pauseReason === "offer_deactivated" &&
                  "Wstrzymana: oferta zdezaktywowana"}
                {campaign.pauseReason === "manual" && "Wstrzymana ręcznie"}
                {campaign.pauseReason === "external" &&
                  `Wstrzymana w ${adSystemLabel(campaign.system)}`}
              </P>
            )}

            {campaign.lastError && (
              <Alert $type="error" $small $marginTop="XS">
                {campaign.lastError}
              </Alert>
            )}

            <Metrics>
              <Metric>
                <P $small>Budżet dzienny</P>
                <H3 $marginBottom="0">{formatMoney(campaign.dailyBudget, campaign.currency)}</H3>
              </Metric>
              <Metric>
                <P $small>Wydano</P>
                <H3 $marginBottom="0">{formatMoney(spent, campaign.currency)}</H3>
                <P
                  $small
                >{`z ${formatMoney(campaign.spendLimit, campaign.currency)} (${progress.toFixed(0)}%)`}</P>
              </Metric>
              <Metric>
                <P $small>Wyświetlenia</P>
                <H3 $marginBottom="0">{formatNumber(campaign.metrics?.impressions)}</H3>
              </Metric>
              <Metric>
                <P $small>Kliknięcia</P>
                <H3 $marginBottom="0">{formatNumber(campaign.metrics?.clicks)}</H3>
              </Metric>
              <Metric>
                <P $small>Konwersje Meta</P>
                <H3 $marginBottom="0">{(campaign.metrics?.conversions ?? 0).toFixed(1)}</H3>
              </Metric>
            </Metrics>

            {campaign.metrics?.lastSyncedAt && (
              <P $small $marginTop="XXS">
                {`Dane z ${formatDate(campaign.metrics.lastSyncedAt, "pl", "dd/MM/yyyy HH:mm")}`}
              </P>
            )}

            <SegmentList>
              {campaign.segments.map(segment => (
                <Box key={segment.name}>
                  <SegmentRow>
                    <P $small>{segment.name}</P>
                    <P $small>
                      {`${formatNumber(segment.metrics?.impressions)} wyśw. · ${segment.ads.length} reklam`}
                    </P>
                  </SegmentRow>
                  {segment.lastError && (
                    <Alert $type="error" $small $marginBottom="XS">
                      {segment.lastError}
                    </Alert>
                  )}
                  {segment.ads
                    .filter(ad => ad.rejectionReason)
                    .map(ad => (
                      <Alert key={ad.creativeKey} $type="error" $small $marginBottom="XS">
                        {`Kreacja ${ad.creativeKey}: ${ad.rejectionReason}`}
                      </Alert>
                    ))}
                </Box>
              ))}
            </SegmentList>

            <ActionsRow $marginTop="XS" $flexWrap="wrap">
              {campaign.status === "failed" && (
                <>
                  <Button
                    $type="secondary"
                    icon="refresh"
                    loading={loadingId === campaign.id}
                    onClick={() => retry(campaign)}
                  >
                    Ponów
                  </Button>
                  <Button
                    $type="secondary"
                    icon="trash"
                    loading={loadingId === campaign.id}
                    onClick={() => removeFailed(campaign)}
                  >
                    Usuń rekord
                  </Button>
                </>
              )}

              {["active", "paused", "budget_exhausted"].includes(campaign.status) && (
                <Button
                  $type="secondary"
                  icon="edit"
                  loading={loadingId === campaign.id}
                  onClick={() => openBudget(campaign)}
                >
                  Zmień budżet
                </Button>
              )}

              {campaign.status === "budget_exhausted" && (
                <Button
                  $type="secondary"
                  icon="boost"
                  loading={loadingId === campaign.id}
                  onClick={() => renew(campaign)}
                >
                  Wznów
                </Button>
              )}

              {campaign.status !== "deactivated" && campaign.status !== "failed" && (
                <Button
                  $type="secondary"
                  icon="trash"
                  loading={loadingId === campaign.id}
                  onClick={() => deactivate(campaign)}
                >
                  Dezaktywuj
                </Button>
              )}
            </ActionsRow>
          </CampaignCard>
        )
      })}

      <Button
        $marginTop="S"
        $marginLeft="auto"
        icon="plus"
        disabled={hasBlocking}
        onClick={() => setShowForm(true)}
      >
        Nowa kampania
      </Button>

      {hasBlocking && (
        <P $small $marginTop="XXS">
          Oferta ma już aktywną kampanię. Dezaktywuj ją, żeby utworzyć nową.
        </P>
      )}

      <Modal
        show={!!budgetCampaign}
        setShow={() => setBudgetCampaign(null)}
        title="Budżet kampanii"
      >
        <Box>
          <Input
            name="budgetDaily"
            label="Budżet dzienny (zł)"
            value={budgetDaily}
            onChange={e => setBudgetDaily(String(e.value))}
            $marginBottom="XS"
          />
          <Input
            name="budgetLimit"
            label="Limit całkowity (zł)"
            value={budgetLimit}
            onChange={e => setBudgetLimit(String(e.value))}
            $marginBottom="XS"
          />
          {budgetCampaign && (
            <P $small $marginBottom="S">
              {`Wydano do tej pory ${(budgetCampaign.metrics?.spend ?? 0).toFixed(2)} ${budgetCampaign.currency}. Podniesienie limitu wznowi kampanię, jeśli była wstrzymana z powodu wyczerpania budżetu.`}
            </P>
          )}
          <ActionsRow>
            <Button $type="secondary" onClick={() => setBudgetCampaign(null)}>
              Anuluj
            </Button>
            <Button icon="check" onClick={saveBudget}>
              Zapisz
            </Button>
          </ActionsRow>
        </Box>
      </Modal>

      <OfferDetailsCampaignForm
        offerId={offerId}
        show={showForm}
        setShow={setShowForm}
        onCreated={refresh}
      />
    </Accordion>
  )
}

export default OfferDetailsCampaigns

const ActionsRow = styled(Row)`
  display: flex;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const CampaignCard = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.S};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: ${({ theme }) => theme.color.background};
`

const Metrics = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-top: ${({ theme }) => theme.space.XS};
`

const Metric = styled(Box)`
  display: flex;
  flex-direction: column;
`

const SegmentList = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.space.XS};
`

const SegmentRow = styled(Row)`
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.XXS} 0;
`
